// Left Sidebar Component - Unified contextual sidebar for hex info and tokens
import { getCurrentRevealState, getCurrentTokenPosition } from '../main.js';
import { calculateDaysElapsed, DEFAULT_GAME_DATE } from '../utils/calendar.js';
import { iconLibrary } from '../utils/icon-library.js';
import { CONFIG } from '../config.js';
import { HexEvents, createHexEvent } from '../utils/hex-events.js';
import { EventManagerMixin } from '../utils/event-manager.js';
import { getTokenInitials, escapeHtml } from '../utils/ui-helpers.js';
import { showConfirmDialog } from '../utils/confirm-modal.js';

class LeftSidebar extends EventManagerMixin(HTMLElement) {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.selectedHex = null;
        this.viewMode = 'gm'; // 'gm' or 'player'
        this.syncChannel = null;
    }

    connectedCallback() {
        // Check for view-mode attribute
        const viewModeAttr = this.getAttribute('view-mode');
        if (viewModeAttr === 'player') {
            this.viewMode = 'player';
        }
        this.render();

        // Setup BroadcastChannel for Player View to receive GM selections
        if (this.viewMode === 'player') {
            this.setupSyncChannel();
        }

        // Listen for hex state changes (e.g., fog toggle via right-click or R key)
        this.addManagedListener(window, HexEvents.HEX_STATE_CHANGED, (e) => {
            this.handleHexStateChanged(e.detail);
        });
    }

    disconnectedCallback() {
        // Clean up BroadcastChannel
        if (this.syncChannel) {
            this.syncChannel.close();
            this.syncChannel = null;
        }
    }

    setupSyncChannel() {
        try {
            this.syncChannel = new BroadcastChannel(CONFIG.BROADCAST_CHANNEL_NAME);

            this.syncChannel.onmessage = (event) => {
                const { type, hexData } = event.data;

                if (type === 'hex-selected') {
                    // In Player View, only show revealed hexes
                    if (this.viewMode === 'player' && hexData?.state) {
                        const isRevealed = getCurrentRevealState(hexData.state);

                        if (!isRevealed) {
                            this.deselectHex(); // Show overview when GM selects unrevealed hex
                            return;
                        }
                    }

                    // Update sidebar to show selected hex
                    this.selectHex(hexData);
                } else if (type === 'hex-deselected') {
                    // Clear hex selection
                    this.deselectHex();
                }
            };
        } catch (e) {
            // BroadcastChannel not supported
            console.warn('BroadcastChannel not available for sidebar sync');
        }
    }

    render() {
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: flex;
                    flex-direction: column;
                    width: 320px;
                    height: 100%;
                    background: var(--bg-surface, #1e1e2e);
                    border-right: 1px solid var(--border-subtle, #333);
                    overflow: hidden;
                }

                .sidebar-section {
                    border-bottom: 1px solid var(--border-subtle, #333);
                }

                .section-header {
                    padding: 0.75rem 1rem;
                    background: var(--bg-elevated, #2a2a3a);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .section-header h3 {
                    margin: 0;
                    font-size: 0.875rem;
                    font-weight: 600;
                    color: var(--text-primary, #e5e5e5);
                }

                .hex-title-group {
                    display: flex;
                    flex-direction: column;
                    gap: 0.125rem;
                }

                .hex-display-id {
                    font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
                    font-size: 1rem;
                    font-weight: 700;
                    color: var(--text-primary, #e5e5e5);
                    letter-spacing: 0.05em;
                }

                .hex-source-id {
                    font-size: 0.7rem;
                    color: var(--text-secondary, #888);
                    font-weight: 400;
                }

                .section-content {
                    padding: 1rem;
                    overflow-y: auto;
                }

                /* Hex Info Section */
                .hex-section {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    min-height: 0;
                }

                .hex-section.disabled {
                    opacity: 0.5;
                }

                .hex-section.disabled .section-content {
                    pointer-events: none;
                }

                .hex-section .section-content {
                    flex: 1;
                    overflow-y: auto;
                }

                /* Tokens Section */
                .tokens-section {
                    max-height: 200px;
                    flex-shrink: 0;
                }

                .tokens-section .section-content {
                    max-height: 140px;
                    overflow-y: auto;
                }

                /* Buttons */
                .btn-small {
                    background: var(--color-primary, #3b82f6);
                    color: white;
                    border: none;
                    padding: 0.25rem 0.5rem;
                    border-radius: 0.25rem;
                    cursor: pointer;
                    font-size: 0.75rem;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                .btn-small:hover {
                    background: var(--color-primary-hover, #2563eb);
                }

                .btn-success {
                    background: var(--color-success, #10b981);
                }

                .btn-success:hover {
                    background: #059669;
                }

                .btn-danger {
                    background: var(--color-danger, #ef4444);
                }

                .btn-danger:hover {
                    background: #dc2626;
                }

                /* Checkbox */
                .checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    margin-bottom: 1rem;
                    font-size: 0.875rem;
                }

                input[type="checkbox"] {
                    width: 18px;
                    height: 18px;
                    cursor: pointer;
                }

                /* Bulk actions */
                .bulk-actions {
                    display: flex;
                    gap: 0.5rem;
                    margin-bottom: 1rem;
                }

                /* Sub-sections for notes/icons */
                .sub-section {
                    margin-bottom: 1rem;
                }

                .sub-section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                }

                .sub-section-title {
                    font-weight: 600;
                    font-size: 0.875rem;
                    color: var(--text-primary, #e5e5e5);
                }

                /* Item cards for notes/icons */
                .item-card {
                    padding: 0.75rem;
                    background: var(--bg-elevated, #2a2a3a);
                    border-radius: 0.25rem;
                    margin-bottom: 0.5rem;
                }

                .item-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.5rem;
                }

                .badge {
                    padding: 2px 8px;
                    border-radius: 4px;
                    font-size: 0.7rem;
                    font-weight: 600;
                }

                .badge-show {
                    background: var(--color-success, #10b981);
                    color: white;
                }

                .badge-secret {
                    background: var(--color-danger, #ef4444);
                    color: white;
                }

                .item-actions {
                    display: flex;
                    gap: 0.5rem;
                }

                .item-actions button {
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-size: 0.75rem;
                    padding: 0;
                }

                .item-actions .edit-btn {
                    color: var(--color-primary, #3b82f6);
                }

                .item-actions .delete-btn {
                    color: var(--color-danger, #ef4444);
                }

                .note-content {
                    margin: 0.5rem 0;
                    white-space: pre-wrap;
                    word-wrap: break-word;
                    font-size: 0.875rem;
                    color: var(--text-primary, #e5e5e5);
                }

                .item-timestamp {
                    color: var(--text-secondary, #888);
                    font-size: 0.7rem;
                }

                .empty-state {
                    color: var(--text-secondary, #888);
                    font-size: 0.8rem;
                    font-style: italic;
                }

                /* Icon display */
                .icon-display {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .icon-display i {
                    font-size: 1.25rem;
                }

                /* Token list */
                .token-item {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    padding: 0.5rem;
                    background: var(--bg-elevated, #2a2a3a);
                    border-radius: 0.25rem;
                    margin-bottom: 0.5rem;
                    cursor: grab;
                    transition: all 0.2s ease;
                }

                .token-item:hover {
                    background: var(--bg-hover, #353545);
                }

                .token-item.selected {
                    background: var(--accent-bg, #3a4a5a);
                    border: 2px solid var(--accent, #4a9eff);
                    cursor: crosshair;
                }

                .token-item.selected::after {
                    content: 'Click hex to place';
                    position: absolute;
                    right: 8px;
                    font-size: 0.7rem;
                    color: var(--accent, #4a9eff);
                    font-style: italic;
                }

                .token-avatar {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.75rem;
                    font-weight: bold;
                    color: white;
                }

                .token-name {
                    flex: 1;
                    font-size: 0.875rem;
                }

                /* Mobile: Bottom sheet */
                @media (max-width: 768px) {
                    :host {
                        position: fixed;
                        bottom: 0;
                        left: 0;
                        right: 0;
                        width: 100%;
                        height: auto;
                        max-height: 70vh;
                        border-right: none;
                        border-top: 1px solid var(--border-subtle, #333);
                        border-radius: 16px 16px 0 0;
                        transform: translateY(calc(100% - 50px));
                        transition: transform 0.3s ease;
                        z-index: ${CONFIG.Z_INDEX_SIDEBAR};
                    }

                    :host(.hex-selected) {
                        transform: translateY(0);
                    }

                    .section-header {
                        border-radius: 16px 16px 0 0;
                    }

                    .tokens-section {
                        display: none;
                    }

                    :host(.hex-selected) .tokens-section {
                        display: block;
                    }
                }
            </style>

            <div class="hex-section sidebar-section ${this.selectedHex ? '' : 'disabled'}">
                <div class="section-header">
                    ${this.selectedHex ? `
                        <div class="hex-title-group">
                            <span class="hex-display-id">Hex ${this.selectedHex.displayId || '????'}</span>
                            <span class="hex-source-id">Source: ${this.selectedHex.coord}</span>
                        </div>
                    ` : '<h3>Select a hex</h3>'}
                </div>
                <div class="section-content" id="hex-content">
                    ${this.renderHexContent()}
                </div>
            </div>

            <div class="tokens-section sidebar-section">
                <div class="section-header">
                    <h3>Tokens</h3>
                    ${this.viewMode === 'gm' ? `
                        <button class="btn-small" id="new-token-btn">
                            <i class="fas fa-plus"></i> New
                        </button>
                    ` : ''}
                </div>
                <div class="section-content" id="token-list">
                    ${this.renderTokenList()}
                </div>
            </div>
        `;

        this.setupEventListeners();
    }

    renderHexContent() {
        if (!this.selectedHex) {
            if (this.viewMode === 'player') {
                return this.renderPlayerOverview();
            }
            return '<p class="empty-state">Click a hex on the map to view and edit its details</p>';
        }

        // Player mode: show read-only hex info
        if (this.viewMode === 'player') {
            return `
                <div class="sub-section">
                    <div class="sub-section-header">
                        <span class="sub-section-title">Notes</span>
                    </div>
                    <div id="notes-list"></div>
                </div>

                <div class="sub-section">
                    <div class="sub-section-header">
                        <span class="sub-section-title">Icons</span>
                    </div>
                    <div id="icons-list"></div>
                </div>
            `;
        }

        // GM mode: show full edit controls
        const isRevealed = this.selectedHex.state ? getCurrentRevealState(this.selectedHex.state) : false;
        return `
            <label class="checkbox-label">
                <input type="checkbox" id="revealed" ${isRevealed ? 'checked' : ''}>
                <span>Revealed to Players</span>
            </label>

            <div class="bulk-actions">
                <button class="btn-small btn-success" id="reveal-all-btn">Reveal All</button>
                <button class="btn-small btn-danger" id="hide-all-btn">Hide All</button>
            </div>

            <div class="sub-section">
                <div class="sub-section-header">
                    <span class="sub-section-title">Notes</span>
                    <button class="btn-small" id="add-note-btn">+ Add</button>
                </div>
                <div id="notes-list"></div>
            </div>

            <div class="sub-section">
                <div class="sub-section-header">
                    <span class="sub-section-title">Icons</span>
                    <button class="btn-small" id="add-icon-btn">+ Add</button>
                </div>
                <div id="icons-list"></div>
            </div>

            <div class="sub-section">
                <div class="sub-section-header">
                    <span class="sub-section-title">Tokens on this hex</span>
                </div>
                <div id="hex-tokens-list">${this.renderHexTokens()}</div>
            </div>
        `;
    }

    renderHexTokens() {
        if (!this.selectedHex) {
            return '<p class="empty-state">No hex selected</p>';
        }

        const tokens = window.currentSession?.tokens || [];
        const hexTokens = tokens.filter(token => {
            const position = getCurrentTokenPosition(token);
            return position &&
                   position.col === this.selectedHex.x &&
                   position.row === this.selectedHex.y;
        });

        if (hexTokens.length === 0) {
            return '<p class="empty-state">No tokens on this hex</p>';
        }

        return `
            <div style="display: flex; flex-direction: column; gap: 0.5rem;">
                ${hexTokens.map(token => {
                    const initials = getTokenInitials(token.name);

                    const avatarContent = token.imageDataUrl
                        ? `<img src="${token.imageDataUrl}" style="width: 100%; height: 100%; border-radius: 50%;" alt="${escapeHtml(token.name)}" />`
                        : initials;

                    const visibilityBadge = token.visibility === CONFIG.VISIBILITY_SHOW
                        ? '<span class="badge badge-show">Show</span>'
                        : '<span class="badge badge-secret">Secret</span>';

                    return `
                        <div style="display: flex; align-items: center; gap: 0.5rem; padding: 0.5rem; background: var(--bg-elevated, #2a2a3a); border-radius: 4px;">
                            <div style="width: 32px; height: 32px; border-radius: 50%; background-color: ${token.color}; display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 0.75rem; font-weight: bold; color: #fff;">
                                ${avatarContent}
                            </div>
                            <div style="flex: 1; min-width: 0;">
                                <div style="font-weight: 600; color: var(--text-primary, #e5e5e5); font-size: 0.875rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                                    ${escapeHtml(token.name)}
                                </div>
                                ${visibilityBadge}
                            </div>
                        </div>
                    `;
                }).join('')}
            </div>
        `;
    }

    renderPlayerOverview() {
        const session = window.currentSession;
        const mapData = window.currentMapData;

        if (!session || !mapData) {
            return '<p class="empty-state">Waiting for GM...</p>';
        }

        const totalHexes = Object.keys(session.hexStates).length;
        const revealedHexes = Object.values(session.hexStates).filter(state => getCurrentRevealState(state)).length;

        return `
            <div style="padding: 1rem;">
                <h3 style="margin: 0 0 1rem 0; color: var(--text-primary);">${session.mapName}</h3>
                <div style="display: flex; flex-direction: column; gap: 0.5rem; color: var(--text-secondary); font-size: 0.875rem;">
                    <div>Total Hexes: <strong style="color: var(--text-primary);">${totalHexes}</strong></div>
                    <div>Revealed: <strong style="color: var(--color-success);">${revealedHexes}</strong></div>
                </div>
                <p style="margin-top: 1.5rem; color: var(--text-secondary); font-size: 0.875rem; font-style: italic;">
                    Waiting for GM to select a hex...
                </p>
            </div>
        `;
    }

    renderTokenList() {
        const tokens = window.currentSession?.tokens || [];

        if (tokens.length === 0) {
            return '<p class="empty-state">No tokens yet</p>';
        }

        // Filter tokens to show only those on the selected hex
        const filteredTokens = this.selectedHex
            ? tokens.filter(token => {
                const position = getCurrentTokenPosition(token);
                return position &&
                       position.col === this.selectedHex.x &&
                       position.row === this.selectedHex.y;
            })
            : tokens; // Show all tokens when no hex selected

        if (filteredTokens.length === 0 && this.selectedHex) {
            return '<p class="empty-state">No tokens on this hex</p>';
        }

        return filteredTokens.map(token => {
            // Calculate initials from name
            const initials = getTokenInitials(token.name);

            // Render token with image or initials
            const avatarContent = token.imageDataUrl
                ? `<img src="${token.imageDataUrl}" style="width: 100%; height: 100%; border-radius: 50%;" alt="${escapeHtml(token.name)}" />`
                : initials;

            // Visibility badge
            const visibilityBadge = token.visibility === CONFIG.VISIBILITY_SHOW
                ? '<span class="badge badge-show">Show</span>'
                : '<span class="badge badge-secret">Secret</span>';

            return `
                <div class="token-item" draggable="true" data-token-id="${token.id}">
                    <div class="token-avatar" style="background-color: ${token.color}">
                        ${avatarContent}
                    </div>
                    <div style="flex: 1; display: flex; flex-direction: column; gap: 0.25rem;">
                        <div style="display: flex; justify-content: space-between; align-items: center;">
                            <div class="token-name">${escapeHtml(token.name)}</div>
                            ${this.viewMode === 'gm' ? `
                                <div class="item-actions">
                                    <button class="edit-btn edit-token-btn" data-token-id="${token.id}">Edit</button>
                                    <button class="delete-btn delete-token-btn" data-token-id="${token.id}">Delete</button>
                                </div>
                            ` : ''}
                        </div>
                        ${visibilityBadge}
                    </div>
                </div>
            `;
        }).join('');
    }

    setupEventListeners() {
        // Listen for read-only state changes
        this.addManagedListener(document, 'readonly-state-changed', (e) => {
            this.updateReadOnlyState(e.detail.isReadOnly);
            // Refresh notes display when time travel state changes
            if (this.selectedHex) {
                this.displayNotes();
                this.displayIcons();
            }
        });

        // New token button
        const newTokenBtn = this.shadowRoot.getElementById('new-token-btn');
        if (newTokenBtn) {
            this.addManagedListener(newTokenBtn, 'click', () => {
                if (this.isReadOnly()) {
                    this.showToast('Cannot edit while viewing history');
                    return;
                }
                this.dispatchEvent(createHexEvent(HexEvents.CREATE_TOKEN));
            });
        }

        // Token drag handlers
        this.setupTokenDragHandlers();

        // Only wire up hex-related listeners if a hex is selected
        if (this.selectedHex) {
            this.setupHexListeners();
        }
    }

    isReadOnly() {
        return window.currentSession?.isReadOnly === true;
    }

    showToast(message) {
        // Dispatch event to main.js to show toast
        document.dispatchEvent(createHexEvent(HexEvents.SHOW_TOAST, {
            message
        }));
    }

    updateReadOnlyState(isReadOnly) {
        // Disable all editing controls
        const buttons = this.shadowRoot.querySelectorAll('button:not(.edit-token-btn):not(.delete-token-btn)');
        buttons.forEach(btn => {
            btn.disabled = isReadOnly;
            btn.style.opacity = isReadOnly ? '0.5' : '1';
            btn.style.cursor = isReadOnly ? 'not-allowed' : 'pointer';
        });

        // Disable revealed checkbox
        const revealedCheckbox = this.shadowRoot.getElementById('revealed');
        if (revealedCheckbox) {
            revealedCheckbox.disabled = isReadOnly;
        }
    }

    setupTokenDragHandlers() {
        const tokenList = this.shadowRoot.getElementById('token-list');
        if (!tokenList) return;

        // Use event delegation for drag events
        this.addManagedListener(tokenList, 'dragstart', (e) => {
            if (e.target.classList.contains('token-item') || e.target.closest('.token-item')) {
                const tokenItem = e.target.classList.contains('token-item')
                    ? e.target
                    : e.target.closest('.token-item');

                const tokenId = tokenItem.dataset.tokenId;
                if (tokenId) {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', tokenId);

                    // Add visual feedback
                    tokenItem.style.opacity = '0.5';
                }
            }
        });

        this.addManagedListener(tokenList, 'dragend', (e) => {
            if (e.target.classList.contains('token-item') || e.target.closest('.token-item')) {
                const tokenItem = e.target.classList.contains('token-item')
                    ? e.target
                    : e.target.closest('.token-item');

                // Remove visual feedback
                tokenItem.style.opacity = '1';
            }
        });
    }

    setupHexListeners() {
        // Revealed checkbox
        const revealedCheckbox = this.shadowRoot.getElementById('revealed');
        if (revealedCheckbox) {
            this.addManagedListener(revealedCheckbox, 'change', (e) => {
                if (this.isReadOnly()) {
                    e.preventDefault();
                    e.target.checked = !e.target.checked; // Revert change
                    this.showToast('Cannot edit while viewing history');
                    return;
                }
                const coord = `${this.selectedHex.x},${this.selectedHex.y}`;

                const hexMap = document.getElementById('main-map');
                if (hexMap && hexMap.setFogState) {
                    // setFogState will update the reveal history
                    hexMap.setFogState(coord, e.target.checked);
                }
            });
        }

        // Reveal All button
        const revealAllBtn = this.shadowRoot.getElementById('reveal-all-btn');
        if (revealAllBtn) {
            this.addManagedListener(revealAllBtn, 'click', () => {
                if (this.isReadOnly()) {
                    this.showToast('Cannot edit while viewing history');
                    return;
                }
                const hexMap = document.getElementById('main-map');
                if (hexMap && hexMap.setAllFogState) {
                    // setAllFogState will update the reveal history for all hexes
                    hexMap.setAllFogState(true);
                    const checkbox = this.shadowRoot.getElementById('revealed');
                    if (checkbox) checkbox.checked = true;
                }
            });
        }

        // Hide All button
        const hideAllBtn = this.shadowRoot.getElementById('hide-all-btn');
        if (hideAllBtn) {
            this.addManagedListener(hideAllBtn, 'click', () => {
                if (this.isReadOnly()) {
                    this.showToast('Cannot edit while viewing history');
                    return;
                }
                const hexMap = document.getElementById('main-map');
                if (hexMap && hexMap.setAllFogState) {
                    // setAllFogState will update the reveal history for all hexes
                    hexMap.setAllFogState(false);
                    const checkbox = this.shadowRoot.getElementById('revealed');
                    if (checkbox) checkbox.checked = false;
                }
            });
        }

        // Add Note button
        const addNoteBtn = this.shadowRoot.getElementById('add-note-btn');
        if (addNoteBtn) {
            this.addManagedListener(addNoteBtn, 'click', () => {
                if (this.isReadOnly()) {
                    this.showToast('Cannot edit while viewing history');
                    return;
                }
                const noteEditor = document.getElementById('note-editor');
                if (noteEditor && this.selectedHex) {
                    const coord = `${this.selectedHex.x},${this.selectedHex.y}`;
                    noteEditor.open(coord);
                }
            });
        }

        // Add Icon button
        const addIconBtn = this.shadowRoot.getElementById('add-icon-btn');
        if (addIconBtn) {
            this.addManagedListener(addIconBtn, 'click', () => {
                if (this.isReadOnly()) {
                    this.showToast('Cannot edit while viewing history');
                    return;
                }
                const iconPicker = document.getElementById('icon-picker');
                if (iconPicker && this.selectedHex) {
                    const coord = `${this.selectedHex.x},${this.selectedHex.y}`;
                    iconPicker.open(coord);
                }
            });
        }

        // Display notes, icons, and tokens
        this.displayNotes();
        this.displayIcons();
        this.refreshTokenList();

        // Apply current read-only state to newly created buttons (in case we're in historical mode)
        if (window.currentSession?.isReadOnly) {
            this.updateReadOnlyState(true);
        }
    }

    selectHex(hexData) {
        this.selectedHex = hexData;
        this.classList.add('hex-selected');
        this.render();
    }

    deselectHex() {
        this.selectedHex = null;
        this.classList.remove('hex-selected');
        this.render();
    }

    handleHexStateChanged(detail) {
        // If the changed hex is the currently selected hex, update the checkbox
        if (!this.selectedHex) return;

        const selectedCoord = `${this.selectedHex.x},${this.selectedHex.y}`;
        if (detail.hexCoord === selectedCoord) {
            // Update the checkbox to reflect new state
            const revealedCheckbox = this.shadowRoot.getElementById('revealed');
            if (revealedCheckbox) {
                revealedCheckbox.checked = detail.revealed;
            }

            // Update the selectedHex state object to stay in sync
            if (this.selectedHex.state) {
                // Get the updated state from the session
                const hexMap = document.getElementById('main-map');
                if (hexMap && hexMap.session && hexMap.session.hexStates) {
                    this.selectedHex.state = hexMap.session.hexStates[selectedCoord];
                }
            }
        }
    }

    displayNotes() {
        if (!this.selectedHex) return;

        const coord = `${this.selectedHex.x},${this.selectedHex.y}`;
        const notesList = this.shadowRoot.getElementById('notes-list');

        if (!notesList) return;

        // In Player View, read from selectedHex.state (synced via BroadcastChannel)
        // In GM View, read from session (authoritative source)
        let notes = this.viewMode === 'player'
            ? (this.selectedHex.state?.notes || [])
            : (window.currentSession?.hexStates[coord]?.notes || []);

        // Player mode: filter to only show "show" visibility notes
        if (this.viewMode === 'player') {
            notes = notes.filter(note => note.visibility === CONFIG.VISIBILITY_SHOW);
        }

        // Time travel mode: filter notes by game date
        if (this.viewMode === 'gm' && window.currentSession?.timeTravelDay) {
            const timeTravelDay = window.currentSession.timeTravelDay;
            const startDate = window.currentSession.calendar?.startDate || DEFAULT_GAME_DATE;

            notes = notes.filter(note => {
                // Notes without gameDate (old format) are always shown
                if (!note.gameDate) return true;

                // Calculate the day this note was created
                const noteDay = calculateDaysElapsed(startDate, note.gameDate);

                // Show note only if it was created before or on the selected day
                return noteDay <= timeTravelDay;
            });
        }

        if (notes.length === 0) {
            notesList.innerHTML = '<p class="empty-state">No notes yet</p>';
        } else {
            const sortedNotes = [...notes].sort((a, b) => b.createdAt - a.createdAt);

            notesList.innerHTML = sortedNotes.map(note => `
                <div class="item-card">
                    <div class="item-header">
                        <span class="badge ${note.visibility === CONFIG.VISIBILITY_SHOW ? 'badge-show' : 'badge-secret'}">
                            ${note.visibility === CONFIG.VISIBILITY_SHOW ? 'Show' : 'Secret'}
                        </span>
                        ${this.viewMode === 'gm' ? `
                            <div class="item-actions">
                                <button class="edit-btn edit-note-btn" data-note-id="${note.id}">Edit</button>
                                <button class="delete-btn delete-note-btn" data-note-id="${note.id}">Delete</button>
                            </div>
                        ` : ''}
                    </div>
                    <p class="note-content">${escapeHtml(note.content)}</p>
                    <span class="item-timestamp">${new Date(note.createdAt).toLocaleString()}</span>
                </div>
            `).join('');
        }

        // Use event delegation - single listener handles all note button clicks
        // Remove old listener if exists, then add new one
        if (this._notesListHandler) {
            notesList.removeEventListener('click', this._notesListHandler);
        }
        this._notesListHandler = (e) => {
            const editBtn = e.target.closest('.edit-note-btn');
            const deleteBtn = e.target.closest('.delete-note-btn');

            if (editBtn) {
                const noteId = editBtn.dataset.noteId;
                const note = notes.find(n => n.id === noteId);
                if (note) {
                    const noteEditor = document.getElementById('note-editor');
                    if (noteEditor) {
                        noteEditor.open(coord, note);
                    }
                }
            } else if (deleteBtn) {
                showConfirmDialog('Delete this note?', () => {
                    const noteId = deleteBtn.dataset.noteId;
                    this.deleteNote(noteId);
                }, {
                    confirmText: 'Delete',
                    cancelText: 'Cancel'
                });
            }
        };
        this.addManagedListener(notesList, 'click', this._notesListHandler);
    }

    deleteNote(noteId) {
        const coord = `${this.selectedHex.x},${this.selectedHex.y}`;
        if (window.currentSession?.hexStates[coord]) {
            const notes = window.currentSession.hexStates[coord].notes || [];
            const index = notes.findIndex(n => n.id === noteId);
            if (index !== -1) {
                notes.splice(index, 1);
                this.displayNotes();
            }
        }
    }


    displayIcons() {
        if (!this.selectedHex) return;

        const iconsList = this.shadowRoot.getElementById('icons-list');
        if (!iconsList) return;

        const coord = `${this.selectedHex.x},${this.selectedHex.y}`;

        // In Player View, read from selectedHex.state (synced via BroadcastChannel)
        // In GM View, read from session (authoritative source)
        let icons = this.viewMode === 'player'
            ? (this.selectedHex.state?.icons || [])
            : (window.currentSession?.hexStates[coord]?.icons || []);

        // Player mode: filter to only show "show" visibility icons
        if (this.viewMode === 'player') {
            icons = icons.filter(icon => icon.visibility === CONFIG.VISIBILITY_SHOW);
        }

        if (icons.length === 0) {
            iconsList.innerHTML = '<p class="empty-state">No icons yet</p>';
        } else {
            // Render icons asynchronously to load SVGs
            this.renderIconsAsync(icons, iconsList);
        }

        // Use event delegation - single listener handles all icon button clicks
        // Remove old listener if exists, then add new one
        if (this._iconsListHandler) {
            iconsList.removeEventListener('click', this._iconsListHandler);
        }
        this._iconsListHandler = (e) => {
            const editBtn = e.target.closest('.edit-icon-btn');
            const deleteBtn = e.target.closest('.delete-icon-btn');

            if (editBtn) {
                const iconId = editBtn.dataset.iconId;
                const icon = icons.find(i => i.id === iconId);
                if (icon) {
                    const iconPicker = document.getElementById('icon-picker');
                    if (iconPicker) {
                        iconPicker.open(coord, icon);
                    }
                }
            } else if (deleteBtn) {
                showConfirmDialog('Delete this icon?', () => {
                    const iconId = deleteBtn.dataset.iconId;
                    this.deleteIcon(iconId);
                }, {
                    confirmText: 'Delete',
                    cancelText: 'Cancel'
                });
            }
        };
        this.addManagedListener(iconsList, 'click', this._iconsListHandler);
    }

    deleteIcon(iconId) {
        const coord = `${this.selectedHex.x},${this.selectedHex.y}`;
        if (window.currentSession?.hexStates[coord]) {
            const icons = window.currentSession.hexStates[coord].icons || [];
            const index = icons.findIndex(i => i.id === iconId);
            if (index !== -1) {
                icons.splice(index, 1);
                this.displayIcons();

                // Refresh icons on the map
                const hexMap = document.getElementById('main-map');
                if (hexMap && hexMap.refreshIcons) {
                    hexMap.refreshIcons();
                }
            }
        }
    }

    async renderIconsAsync(icons, iconsList) {
        // Render each icon card with SVG loaded from icon library
        const iconCards = await Promise.all(icons.map(async (icon) => {
            let svgContent = '';
            try {
                const svgData = await iconLibrary.loadSvg(icon.iconName);
                if (svgData && svgData.path) {
                    // Create inline SVG with color applied
                    svgContent = `<svg width="24" height="24" viewBox="${svgData.viewBox || '0 0 512 512'}" style="fill: ${icon.color};"><path d="${svgData.path}"/></svg>`;
                }
            } catch (error) {
                console.warn(`Failed to load icon ${icon.iconName}:`, error);
                svgContent = '<span style="color: #ef4444;">⚠</span>';
            }

            return `
                <div class="item-card">
                    <div class="item-header">
                        <span class="icon-display">
                            ${svgContent}
                            <span class="badge ${icon.visibility === CONFIG.VISIBILITY_SHOW ? 'badge-show' : 'badge-secret'}">
                                ${icon.visibility === CONFIG.VISIBILITY_SHOW ? 'Show' : 'Secret'}
                            </span>
                        </span>
                        ${this.viewMode === 'gm' ? `
                            <div class="item-actions">
                                <button class="edit-btn edit-icon-btn" data-icon-id="${icon.id}">Edit</button>
                                <button class="delete-btn delete-icon-btn" data-icon-id="${icon.id}">Delete</button>
                            </div>
                        ` : ''}
                    </div>
                    <span class="item-timestamp">${new Date(icon.createdAt).toLocaleString()}</span>
                </div>
            `;
        }));

        iconsList.innerHTML = iconCards.join('');
    }

    refreshTokenList() {
        const tokenList = this.shadowRoot.getElementById('token-list');
        if (!tokenList) return;

        tokenList.innerHTML = this.renderTokenList();

        // Use event delegation - single listener handles all token button clicks
        // Remove old listener if exists, then add new one
        if (this._tokenListHandler) {
            tokenList.removeEventListener('click', this._tokenListHandler);
        }
        this._tokenListHandler = (e) => {
            const editBtn = e.target.closest('.edit-token-btn');
            const deleteBtn = e.target.closest('.delete-token-btn');
            const tokenItem = e.target.closest('.token-item');

            if (editBtn) {
                const tokenId = editBtn.dataset.tokenId;
                const token = window.currentSession?.tokens?.find(t => t.id === tokenId);
                if (token) {
                    this.dispatchEvent(createHexEvent(HexEvents.EDIT_TOKEN, { token }));
                }
                return; // Don't process token selection
            }

            if (deleteBtn) {
                const tokenId = deleteBtn.dataset.tokenId;
                const token = window.currentSession?.tokens?.find(t => t.id === tokenId);
                if (token) {
                    showConfirmDialog(`Delete token "${token.name}"?`, () => {
                        this.deleteToken(tokenId);
                    }, {
                        confirmText: 'Delete',
                        cancelText: 'Cancel'
                    });
                }
                return; // Don't process token selection
            }

            // Handle token item click (for placement mode)
            if (tokenItem && this.viewMode === 'gm') {
                const tokenId = tokenItem.dataset.tokenId;
                const token = window.currentSession?.tokens?.find(t => t.id === tokenId);
                if (token) {
                    // Toggle selection
                    const isCurrentlySelected = tokenItem.classList.contains('selected');

                    // Clear all other selections
                    const allTokenItems = this.shadowRoot.querySelectorAll('.token-item');
                    allTokenItems.forEach(item => item.classList.remove('selected'));

                    if (!isCurrentlySelected) {
                        // Select this token
                        tokenItem.classList.add('selected');
                        this.dispatchEvent(createHexEvent(HexEvents.TOKEN_SELECTED, { tokenId, token }));
                    } else {
                        // Deselect
                        this.dispatchEvent(createHexEvent(HexEvents.TOKEN_DESELECTED));
                    }
                }
            }
        };
        this.addManagedListener(tokenList, 'click', this._tokenListHandler);
    }

    displayTokens() {
        this.refreshTokenList();
    }

    deleteToken(tokenId) {
        if (!window.currentSession?.tokens) return;

        const index = window.currentSession.tokens.findIndex(t => t.id === tokenId);
        if (index !== -1) {
            window.currentSession.tokens.splice(index, 1);
            this.refreshTokenList();
        }
    }
}

customElements.define('left-sidebar', LeftSidebar);

export default LeftSidebar;
